import { getApiUrl, gridQuery, handler, HandlerBody, parseJson, parseMsDate, secondsToTimestamp } from '.';
import { Result } from '..';
import * as model from '../model';

async function queueHandlerRaw(body: HandlerBody, ucStatus = true) {
  return await handler('/Handlers/QueuesHandler.ashx', body, ucStatus)
}

async function queueHandler<T>(body: HandlerBody, ucStatus = true) {
  return parseJson<T>(await queueHandlerRaw(body, ucStatus))
}


export async function getQueues(region: Exclude<model.QueueRegionHandle, model.QueueRegionId.Squads>, virtuals?: boolean) {
  if (region === model.QueueRegionId.Favourites) {
    return (await gridQuery('queues', 'favourites', '*', model.EventSubscription.Add, '', 'false')).rows.map(mapQueue)
  } else if (region === model.QueueRegionId.Mine) {
    return (await gridQuery('myqueues', 'myqueues', '*', model.EventSubscription.Add)).rows.map(mapQueue)
  } else {
    return (await gridQuery('queues', 'regions', region.server, model.EventSubscription.Add, region.id, virtuals ? 'true' : '')).rows.map(mapQueue)
  }
}

export async function getQueuesByIds({ server, id }: model.Identifier, queueId: model.Identifier[]) {
  let ids = ''
  queueId.forEach(x => {
    ids += x.server + '|' + x.id + ','
  });
  return (await gridQuery('queues', 'list', server, model.EventSubscription.Add, '', ids, id, '', 200)).rows.map(mapQueue)
}

export function getQueueMode(routeType: number): model.QueueMode {
  return routeType === 2 || routeType === 6 || routeType === 5 ? 'manual' : 'auto'
}

function mapQueue(row: any): model.QueueStats {
  return {
    id: String(row.number),
    server: row.serverName,
    type: 'queue',
    icon: row.iconEnum,
    description: row.description,
    mode: getQueueMode(row.routeType),
    status: row.routeToStringLong,
    count: row.queueItemsCount,
    time: row.queueItemsLongestQueueTimeSeconds ? secondsToTimestamp(row.queueItemsLongestQueueTimeSeconds) : null,
    agents: row.agentsLoggedIn,
    answer: row.ansCount,
    abandoned: row.abnQCount,
    other: row.otherCount,
    agentStatus: Math.round(row.serviceLevel),
    virtual: row.virtualQueue,
    voicemailCount: row.voicemailCount,
    ansQTimeTotal: row.ansQTimeTotal,
    abnQTimeTotal: row.abnQTimeTotal,
    toaster: row.iconEnum,
    hide: false,
  }
}

type Scripts = [
  string,
  {
    scriptsCategories: {
      name: string,
      agentScripts: {
        title: string,
        body: string,
      }[]
    }[]
  },
]

export async function getScripts(mediaId: string, queue: string, number: string) {
  if (!queue) { return [] }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [profileUrl, { scriptsCategories }] = await queueHandler<Scripts>({
    command: 'scripts',
    id: queue,
    number,
    callId: mediaId,
  })

  return scriptsCategories.map<model.ScriptFolder>(sc => ({
    name: sc.name,
    items: sc.agentScripts.map(s => ({
      name: s.title,
      html: s.body,
    })),
  }))
}

export async function getQueueDetails(queue: string) {
  return await queueHandler<model.Queue>({ command: 'queue', id: queue })
}

export async function getQueue(queue: string) {
  return mapQueue(await queueHandler({ command: 'stats', id: queue }))
}

interface Email {
  clientPath: string
  id: string
  itemId: string
  originalId: string
  originalItemId: string
  subject: string
  sentAtUtc?: string
  receivedAtUtc: string
  conversationId: string
  from: model.Recipient
  sender: model.Recipient
  receivedBy: model.Recipient
  toRecipients: model.Recipient[]
  ccRecipients: model.Recipient[]
  attachments: model.Attachment[]
  body: string
  style: model.ComposeStyle
  extensionId: string
  extensionDescription: string
  isOutGoing: number
  isAnswered: number
  queueId?: string
  queueDescription?: string
  queuingState: model.QueuingState
  signatureTemplatePath?: string
  defaultEmailFontFamily?: string
  datastoreItemId?: string
}

// id = command.KEYS.DSLKey;
export async function getQueueEmailHeader(emailId: string, accountId?: string): Promise<Email> {
  const e = await queueHandler<Email>({ command: 'get-email', id: emailId, data: !accountId ? '' : accountId })
  return e
}

export async function getQueueEmailByEmailId(emailId: string, accountId?: string): Promise<Email> {
  // getQueueEmailHeader needs to happen first so that the email is copied to the server
  // then getEmailBody to get the copied email also sometiems we are using callId only to get email (current history)
  const email = await getQueueEmailHeader(emailId, accountId)
  // sometimes this call will not have an email id so using the one we get back
  // happens when viewing history
  const body = await getEmailBody(email.id)

  return { ...email, body }
}

/**
 * @param id `command.KEYS.DSLKey`
 * @param path `command.Specific.SignatureTemplatePath`
 */
export async function newReplyEmail(id: string, callId: string, queue: string, replyType: model.EmailReplyType, path: string, defaultFont: string, senderAddress: string = "") {
  const email = await queueHandler<model.Email>({ command: 'new-reply', id, number: callId, action: replyType.toString(), path, queue, defaultFont, email: senderAddress })
  delete (email as any).lastUpdated
  return email
}

export async function newOutGoingEmail(id: string, callId: string, queue: string, path: string, defaultFont: string, senderAddress: string = "") {
  const email = await queueHandler<model.Email>({ command: 'new-outgoing', id, number: callId, path, queue, defaultFont, email: senderAddress })
  delete (email as any).lastUpdated
  return email
}

interface RequestResponse {
  resultCode: number
  resultDescription: string
}

export async function cancelEmail(id: string) {
  const result = await queueHandler<RequestResponse>({ command: 'cancel-email', id })
  return result
}


interface RequestOutGoingEmail {
  resultCode: number
  resultDescription: string
  datastoreItemId: string
  id: string
}

export async function requestOutGoingEmail(queue: string) {
  const e = await queueHandler<RequestOutGoingEmail>({ command: 'request-outgoing', queue })

  return {
    resultCode: e.resultCode,
    resultDescription: e.resultDescription,
    mediaId: e.datastoreItemId,
    id: e.id,
  }
}

export async function getEmailBody(id: string) {
  const url = getEmailBaseUrl(id) + 'body.html';
  console.info('get - ' + url)
  const resp = await fetch(url)
  const text = await resp.text()

  return fixEmailImages(id, text);
}

function getEmailBaseUrl(id: string) {
  return getApiUrl(`/Uploads/Queue/Email/${id}/`);
}

function fixEmailImages(id: string, text: string) {
  const url = getEmailBaseUrl(id);

  const html = new DOMParser().parseFromString(text, 'text/html')
  const base = html.createElement('base')
  base.setAttribute('href', url)
  html.head.appendChild(base)
  return html.documentElement.outerHTML
}

export async function sendQueueEmail(queue: string, email: model.Email, mediaId: string) {
  const e = await queueHandler<Result>({ command: 'send-email', queue, email: JSON.stringify(email), callid: mediaId })
  return e
}


export async function sendChatMessage(sessionId: string, text: string) {
  await queueHandler({ command: 'tc-send', id: sessionId, data: text })
}

async function sendChatAction(sessionId: string, action: model.ChatAction, data: string) {
  await queueHandler({ command: 'tc-action', id: sessionId, action: action.toString(), data: data })
}

export async function sendChatActionTyping(sessionId: string, typing: boolean) {
  await sendChatAction(sessionId, model.ChatAction.UserIsTyping, typing ? '1' : '0')
}

export async function sendChatActionEndSession(sessionId: string) {
  await sendChatAction(sessionId, model.ChatAction.EndSession, '')
}

export type WebChat = {
  chatId: string
  createdAtUtc: string
  queueId: string
  extensionId: string
  extensionName: string
  userId: string
  userName: string
  subject: string
  messages: WebChatMessages[]
}

export type WebChatMessages = {
  to: string
  from: string
  createdAtUtc: string
  message: string
}

export async function getChatData(chatId: string) {
  const e = await queueHandler<WebChat>({ command: 'tc-get', id: chatId })

  const messages: model.WebChatMessages[] = e.messages.map(m => ({
    to: m.to,
    from: m.from,
    createdAtUtc: parseMsDate(m.createdAtUtc),
    message: m.message,

  }) as model.WebChatMessages)

  return {
    chatId: e.chatId,
    createdAtUtc: parseMsDate(e.createdAtUtc),
    queueId: e.queueId,
    extensionId: e.extensionId,
    extensionName: e.extensionName,
    userId: e.userId,
    userName: e.userName,
    subject: e.subject,
    messages: messages,
  } as model.WebChat
}

interface SendTweetResponse {
  success: boolean
  tweetId: string
}

export async function sendTweet(key: string, accountId: string, text: string, queueId: string) {
  return await queueHandler<SendTweetResponse>({ command: 'tweet-send', id: key, number: accountId, text, queue: queueId });
}

export async function outboundDial(medaiId: string, countactId: string, number: string) {
  await queueHandler({ command: 'out-dial', id: medaiId, number: countactId, data: number });
}

export async function outboundSetCode(medaiId: string, contactId: string, codeId: string, codeAction: number, date: string = "") {
  //date needs to be UTC yyyy-MM-ddTHH:mm or ''
  await queueHandler({ command: 'out-set', id: medaiId, number: contactId, data: codeId, type: codeAction.toString(), date });
}

interface DispositionCode {
  iD: string
  name: string
  description: string
  action: number
  deferMinutes: number
  canEditDeferDateTime: boolean
  isDeleted: boolean
  displayAfterDispositionCodeId?: string
}

export async function getOutboundCodes(medaiId: string, campaignId: string): Promise<model.DispositionCode[]> {
  const e = await queueHandler<DispositionCode[]>({
    command: 'out-code',
    id: medaiId,
    number: campaignId,
  })

  return e
}
export async function requestOutboundtData(medaiId: string, contactId: string) {
  const o = await queueHandler<model.Outbound>({ command: 'out-get', id: medaiId, number: contactId })
  const sessions = o.sessions.map(x => {

    const startedAtUtc = typeof x.startedAtUtc === 'string' ? parseMsDate(x.startedAtUtc) : x.startedAtUtc
    const finishedAtUtc = typeof x.finishedAtUtc === 'string' ? parseMsDate(x.finishedAtUtc) : x.finishedAtUtc
    const deferredUntilUtc = typeof x.deferredUntilUtc === 'string' ? parseMsDate(x.deferredUntilUtc) : x.deferredUntilUtc

    // eslint-disable-next-line array-callback-return
    if (!startedAtUtc || !finishedAtUtc) return null

    return {
      ...x,
      startedAtUtc,
      finishedAtUtc,
      deferredUntilUtc,
    }
  })

  return {
    ...o,
    sessions: sessions.flatMap(x => x ? [x]: []),
  }
}

interface FacebookPost {
  postId: string
  createdAtUtc: string
  type: string
  userId: string
  userName: string
  text: string
  linkUrl: string
  linkPicture: string
  linkName: string
  linkCaption: string
  linkDescription: string
  linkTitle: string
  pageId: string
  unRead: boolean
  profilePic: string
  comments: FacebookComment[]
}

interface FacebookComment {
  commentId: string
  createdAtUtc: string
  userId: string
  userName: string
  text: string
  postId: string
  parentCommentId: string
  unRead: boolean
  extensionId: string
  attachmentType: string
  attachmentUrl: string
  attachmentTitle: string
  attachmentDescription: string
  attachmentImage: string
  replies: FacebookComment[]
}

interface FacebookPage {
  pageId: string
  name: string
  url: string
}

interface FacebookRequest {
  posts: FacebookPost[]
  page: FacebookPage
}


export async function facebookPostsGet(key: string, queueId: string) {
  const e = await queueHandler<FacebookRequest>({ command: 'fc-get', id: key, queue: queueId });

  return {
    page: e.page,
    posts: e.posts,
  }
}

interface FacebookUserRequest {
  id: string
  parentId: string
  name: string
}

export async function facebookUserGet(key: string, queueId: string, userId: string, postId: string, commentId: string) {
  const e = await queueHandler<FacebookUserRequest>({ command: 'fc-user', id: key, queue: queueId, uid: userId, p: postId !== '' ? postId : commentId !== '' ? commentId : 'error' });

  return e.name
}

export async function facebookSendPost(key: string, queueId: string, pageId: string, postId: string, commentId: string, text: string) {
  await queueHandler({ command: 'fc-send', id: key, queue: queueId, pa: pageId, po: postId, c: commentId, t: text });
}

export async function killMedia(device: string, callId: string, queue: string) {
  await queueHandlerRaw({ command: 'kill', id: device, number: callId, queue })
}

interface TakeMediaItemResponse {
  resultCode: number
  resultDescription: string
  datastoreItemId: string
}

export async function takeMediaItem(itemId: string, type: model.MediaType) {
  const e = await queueHandler<TakeMediaItemResponse>({ command: 'take-media', id: itemId, type: type });

  return {
    resultCode: e.resultCode,
    resultDescription: e.resultDescription,
    datastoreItemId: e.datastoreItemId,
  }
}

export async function closeMediaItem(medaiId: string) {
  queueHandler({ command: 'close-media', id: medaiId });
}

export type Tweet = {
  userScreenName: string
  userId: string
  userLocation: string
  userFollowersCount: string
  userProfileImageUrl: string
  createdAtUtc: string
  subject: string
  receivedBy: string
  queueId: string
  replies: TweetReply[]
}

export type TweetReply = {
  tweetId: string
  createdAtUtc: string
  text: string
  extensionId: string
  conversationId: string
}


export async function requestTweetData(tweetId: string, queueId: string, reply: boolean = false) {
  const t = await queueHandler<Tweet>({ command: 'tweet-get', id: tweetId, queue: queueId, reply: (reply ? '1' : '0') });

  const x = t.replies.map(r => ({
    tweetId: r.tweetId,
    createdAtUtc: parseMsDate(r.createdAtUtc),
    text: r.text,
    extensionId: r.extensionId,
    conversationId: r.conversationId,
  }))

  return {
    tweeterId: tweetId,
    userScreenName: t.userScreenName,
    userId: t.userId,
    userLocation: t.userLocation,
    userFollowersCount: t.userFollowersCount,
    userProfileImageUrl: t.userProfileImageUrl,
    createdAtUtc: parseMsDate(t.createdAtUtc),
    subject: t.subject,
    receivedBy: t.receivedBy,
    queueId: t.queueId,
    replies: x,

  } as model.Tweet
}

export async function getMyQueueEmails() {
  return await queueHandler<model.QueueEmail[]>({ command: 'from-addresses-mine' })
}

export async function getQueueEmails() {
  return await queueHandler<model.QueueEmail[]>({ command: 'from-addresses-all' })
}

export async function setGosGrpahLimits(values: model.GosLimit[]) {
  queueHandlerRaw({ command: 'graphs', data: JSON.stringify(values) });
}

export async function getGosGrpahLimits() {
  return await queueHandler<model.GosLimit[]>({ command: 'graphs' });
}
